import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Timeline from "@/components/Timeline";
import JsonViewer from "@/components/JsonViewer";
import { toast } from "sonner";
import { RefreshCw, FileText, Bike as BikeIcon, User, AlertOctagon, RotateCcw, AlertTriangle } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
function fmtEUR(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

export default function TransactionDetailDrawer({ transactionId, open, onOpenChange, onChanged }) {
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    if (!transactionId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/transactions/${transactionId}`);
      setTx(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && transactionId) load();
    if (!open) setTx(null);
    // eslint-disable-next-line
  }, [open, transactionId]);

  const onRetry = async () => {
    setRetrying(true);
    try {
      await api.post(`/admin/transactions/${transactionId}/retry`);
      toast.success("Reintento ejecutado");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(false);
    }
  };

  const onResetDeadLetter = async () => {
    setResetting(true);
    try {
      await api.post(`/admin/transactions/${transactionId}/reset-dead-letter`);
      toast.success("Dead-letter reseteado — elegible para auto-retry");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[560px] overflow-y-auto bg-white p-0"
        data-testid="transaction-detail-drawer"
      >
        <SheetHeader className="p-6 pb-3 border-b border-slate-200">
          <SheetTitle className="text-base font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            Detalle de transacción
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="p-6 text-sm text-slate-500" data-testid="drawer-loading">Cargando…</div>
        )}

        {tx && !loading && (
          <div className="p-6 space-y-6">
            {/* summary */}
            <section className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">rental_id</div>
                  <div className="text-sm font-mono font-semibold text-slate-900" data-testid="drawer-rental-id">{tx.rental_id}</div>
                </div>
                <StatusBadge status={tx.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Info label="transaction_id" value={tx.id} mono />
                <Info label="policy_id" value={tx.policy_id || "—"} mono testId="drawer-policy-id" />
                <Info label="adapter_mode" value={tx.adapter_mode} />
                <Info label="intentos" value={`${tx.attempts}${tx.dead_letter ? " (dead-letter)" : ""}`} testId="drawer-attempts" />
                <Info label="próximo reintento" value={tx.dead_letter ? "—" : (tx.next_retry_at ? fmtDate(tx.next_retry_at) : "—")} testId="drawer-next-retry" />
                <Info label="cobertura desde" value={fmtDate(tx.coverage_start)} />
                <Info label="cobertura hasta" value={fmtDate(tx.coverage_end)} />
                <Info label="valor asegurado" value={fmtEUR(tx.bike_value_eur)} />
                <Info label="creada" value={fmtDate(tx.created_at)} />
              </div>
              {tx.certificate_url && (
                <a
                  href={tx.certificate_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="drawer-certificate-link"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 underline underline-offset-4"
                >
                  <FileText className="w-3.5 h-3.5" /> Ver certificado
                </a>
              )}
            </section>

            {/* customer + bike */}
            <section className="grid grid-cols-1 gap-3">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1.5">
                  <User className="w-3 h-3" /> Cliente (datos enmascarados)
                </div>
                <div className="text-sm text-slate-900 font-medium">{tx.customer_masked?.full_name}</div>
                <div className="text-xs text-slate-600 font-mono">
                  doc: {tx.customer_masked?.document_id_masked} · email: {tx.customer_masked?.email_masked}
                </div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1.5">
                  <BikeIcon className="w-3 h-3" /> Bici
                </div>
                <div className="text-sm text-slate-900 font-medium">
                  {tx.raw_input?.bike?.brand} {tx.raw_input?.bike?.model}
                </div>
                <div className="text-xs text-slate-600 font-mono">
                  {tx.raw_input?.bike?.id} · {tx.raw_input?.bike?.type} · {fmtEUR(tx.raw_input?.bike?.value_eur)}
                </div>
              </div>
            </section>

            {/* dead-letter banner */}
            {tx.dead_letter && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200"
                data-testid="dead-letter-banner"
              >
                <AlertOctagon className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs">
                  <div className="font-bold text-red-900 uppercase tracking-wide">Dead-letter</div>
                  <p className="text-red-800 mt-0.5">
                    Se alcanzó el máximo de reintentos. El worker automático ya no la procesará.
                    Resetea para volver a habilitarla.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-red-300 text-red-800 hover:bg-red-100"
                  data-testid="reset-dead-letter-button"
                  onClick={onResetDeadLetter}
                  disabled={resetting}
                >
                  <RotateCcw className={`w-3 h-3 mr-1 ${resetting ? "animate-spin" : ""}`} />
                  {resetting ? "Reseteando…" : "Resetear"}
                </Button>
              </div>
            )}

            {/* last_error block */}
            {tx.last_error && !tx.dead_letter && (
              <div
                className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200"
                data-testid="last-error-block"
              >
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-xs">
                  <div className="font-bold text-amber-900 uppercase tracking-wide">Último error</div>
                  <p className="font-mono text-amber-900 mt-0.5 break-all">{tx.last_error}</p>
                  {tx.next_retry_at && (
                    <p className="text-amber-700 mt-1 text-[11px]">
                      Próximo reintento automático: <span className="font-mono">{fmtDate(tx.next_retry_at)}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* actions */}
            {(tx.status === "failed" || tx.status === "pending") && (
              <Button
                onClick={onRetry}
                disabled={retrying}
                data-testid="retry-transaction-button"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? "animate-spin" : ""}`} />
                {retrying ? "Reintentando…" : "Reintentar manualmente"}
              </Button>
            )}

            {/* timeline */}
            <section>
              <h3 className="text-xs font-bold tracking-wide uppercase text-slate-500 mb-3">Timeline</h3>
              <Timeline events={tx.timeline || []} />
            </section>

            {/* JSON payloads */}
            <section className="space-y-3">
              <JsonViewer testId="drawer-json-input" label="Payload de entrada (Evolvo)" value={tx.raw_input} />
              <JsonViewer testId="drawer-json-mapped" label="Payload mapeado (Hoomu)" value={tx.mapped_payload} />
              <JsonViewer testId="drawer-json-response" label="Última respuesta del adaptador" value={tx.last_response} />
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Info({ label, value, mono = false, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xs ${mono ? "font-mono" : ""} text-slate-900 break-all`}>{value}</div>
    </div>
  );
}
